import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
  accent:'#3AAF58', accentLight:'#EAF7EE',
  bg:'#F3F6F3', surface:'#FFFFFF',
  border:'#E2E8E2', border2:'#CDD8CD',
  text:'#1A2E1E', text2:'#3D5E44', text3:'#7A9A80',
  amber:'#D4840F', amberBg:'#FEF6E7', amberBorder:'#F0D499',
  red:'#C83333', redBg:'#FDEAEA', redBorder:'#F0B8B8',
  blue:'#2563A8', blueBg:'#EAF1FB', blueBorder:'#B8D0F0',
};

const Card = ({ children, style={} }) => (
  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,.05)', ...style }}>{children}</div>
);

const Btn = ({ children, onClick, variant='primary', style={}, disabled }) => {
  const v = {
    primary:{ background:C.accent, color:'#fff', border:'none' },
    danger: { background:C.red, color:'#fff', border:'none' },
    ghost:  { background:'transparent', color:C.text2, border:`1px solid ${C.border2}` },
    light:  { background:C.accentLight, color:C.accent, border:'none' },
  };
  return <button onClick={onClick} disabled={disabled}
    style={{ ...v[variant], padding:'8px 16px', borderRadius:8, fontSize:13, fontWeight:600,
      cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.5:1,
      display:'inline-flex', alignItems:'center', gap:6, whiteSpace:'nowrap', ...style }}>{children}</button>;
};

const Inp = ({ label, value, onChange, type='text', placeholder='', opts }) => {
  const base = { width:'100%', padding:'9px 12px', background:C.bg, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, boxSizing:'border-box' };
  return (
    <div style={{ marginBottom:12 }}>
      {label && <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:5 }}>{label}</div>}
      {opts
        ? <select style={base} value={value} onChange={e=>onChange(e.target.value)}>
            {opts.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
          </select>
        : <input type={type} style={base} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} />}
    </div>
  );
};

const Modal = ({ title, onClose, children, width='480px' }) => (
  <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
    <Card style={{ width, maxWidth:'100%', maxHeight:'90vh', overflow:'auto', padding:28 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:17, fontWeight:800, color:C.text }}>{title}</div>
        <button onClick={onClose} style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, width:32, height:32, cursor:'pointer', color:C.text2 }}>X</button>
      </div>
      {children}
    </Card>
  </div>
);

const Avatar = ({ name, size=38 }) => {
  const initials = name?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase() || '??';
  const colors = ['#3AAF58','#2563A8','#D4840F','#C83333','#6B48A8','#1A7A7A'];
  const color = colors[name?.charCodeAt(0)%colors.length] || C.accent;
  return <div style={{ width:size, height:size, borderRadius:'50%', background:color, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:size*0.35, fontWeight:700, flexShrink:0 }}>{initials}</div>;
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwdUser, setPwdUser] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [newPwd, setNewPwd] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const initForm = { name:'', email:'', phone:'', role_id:'', status:'active', password:'' };
  const [form, setForm] = useState(initForm);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        fetch(`${API}/sessions/users`).then(r=>r.json()),
        fetch(`${API}/sessions/roles`).then(r=>r.json()),
      ]);
      setUsers(Array.isArray(usersRes) ? usersRes : []);
      setRoles(Array.isArray(rolesRes) ? rolesRes : []);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showMsg = (m) => { setMsg(m); setTimeout(()=>setMsg(''), 3000); };

  const addUser = async () => {
    if (!form.name || !form.email || !form.role_id) return showMsg('Name, email and role are required');
    setSaving(true);
    try {
      const res = await fetch(`${API}/sessions/users`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ ...form, password: form.password || 'Botanix@123' })
      });
      const data = await res.json();
      if (res.ok) { setShowAdd(false); setForm(initForm); load(); showMsg('User added successfully!'); }
      else showMsg(data.error || 'Failed to add user');
    } catch(e) { showMsg('Error adding user'); }
    setSaving(false);
  };

  const updateUser = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/sessions/users/${editUser.id}`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ name:form.name, email:form.email, phone:form.phone, role_id:form.role_id, status:form.status })
      });
      if (res.ok) { setEditUser(null); load(); showMsg('User updated successfully!'); }
      else showMsg('Failed to update user');
    } catch(e) { showMsg('Error updating user'); }
    setSaving(false);
  };

  const changePassword = async () => {
    if (!newPwd || newPwd.length < 6) return showMsg('Password must be at least 6 characters');
    setSaving(true);
    try {
      const res = await fetch(`${API}/sessions/users/${pwdUser.id}/password`, {
        method:'PUT', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ password: newPwd })
      });
      if (res.ok) { setPwdUser(null); setNewPwd(''); showMsg('Password changed successfully!'); }
      else showMsg('Failed to change password');
    } catch(e) { showMsg('Error changing password'); }
    setSaving(false);
  };

  const toggleStatus = async (u) => {
    const newStatus = u.status === 'active' ? 'inactive' : 'active';
    await fetch(`${API}/sessions/users/${u.id}`, {
      method:'PUT', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ name:u.name, email:u.email, phone:u.phone, role_id:u.role_id, status:newStatus })
    });
    load();
  };

  const deleteUser = async () => {
    await fetch(`${API}/sessions/users/${confirmDel.id}`, { method:'DELETE' });
    setConfirmDel(null); load(); showMsg('User deleted');
  };

  const openEdit = (u) => {
    setForm({ name:u.name, email:u.email, phone:u.phone||'', role_id:u.role_id||'', status:u.status });
    setEditUser(u);
  };

  const filtered = users.filter(u => {
    const matchSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = !filterRole || u.role_id === parseInt(filterRole);
    const matchStatus = !filterStatus || u.status === filterStatus;
    return matchSearch && matchRole && matchStatus;
  });

  const activeCount = users.filter(u=>u.status==='active').length;
  const inactiveCount = users.filter(u=>u.status!=='active').length;

  const roleOpts = [{ value:'', label:'Select Role' }, ...roles.map(r=>({ value:r.id, label:r.role_name }))];

  return (
    <div style={{ padding:'24px 28px', background:C.bg, minHeight:'100vh' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, color:C.text }}>User Management</div>
          <div style={{ fontSize:13, color:C.text3, marginTop:4 }}>{users.length} total users · {activeCount} active · {inactiveCount} inactive</div>
        </div>
        <Btn onClick={()=>{ setForm(initForm); setShowAdd(true); }}>+ Add User</Btn>
      </div>

      {/* Message */}
      {msg && (
        <div style={{ padding:'12px 16px', borderRadius:10, marginBottom:16, fontSize:13, fontWeight:600,
          background: msg.includes('success')||msg.includes('added')||msg.includes('updated')||msg.includes('changed') ? C.accentLight : C.redBg,
          color: msg.includes('success')||msg.includes('added')||msg.includes('updated')||msg.includes('changed') ? C.accent : C.red,
          border: `1px solid ${msg.includes('success')||msg.includes('added')||msg.includes('updated')||msg.includes('changed') ? C.accent : C.red}` }}>
          {msg}
        </div>
      )}

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:20 }}>
        {[
          ['Total Users', users.length, C.accent, C.accentLight],
          ['Active', activeCount, C.accent, C.accentLight],
          ['Inactive', inactiveCount, C.red, C.redBg],
          ['Roles', roles.length, C.blue, C.blueBg],
        ].map(([label, value, color, bg]) => (
          <Card key={label} style={{ padding:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:'uppercase', marginBottom:6 }}>{label}</div>
            <div style={{ fontSize:24, fontWeight:800, color }}>{value}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name or email..."
          style={{ padding:'8px 14px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, width:220 }} />
        <select value={filterRole} onChange={e=>setFilterRole(e.target.value)}
          style={{ padding:'8px 12px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13 }}>
          <option value="">All Roles</option>
          {roles.map(r=><option key={r.id} value={r.id}>{r.role_name}</option>)}
        </select>
        <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
          style={{ padding:'8px 12px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13 }}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <Btn variant="ghost" onClick={load} style={{ padding:'8px 14px' }}>Refresh</Btn>
      </div>

      {/* Users Grid */}
      {loading ? <div style={{ textAlign:'center', padding:60, color:C.text3 }}>Loading users...</div> : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14 }}>
          {filtered.length===0 ? (
            <Card style={{ padding:40, textAlign:'center', color:C.text3, gridColumn:'1/-1' }}>No users found</Card>
          ) : filtered.map(u => (
            <Card key={u.id} style={{ padding:20, borderLeft:`4px solid ${u.status==='active'?C.accent:C.red}` }}>
              <div style={{ display:'flex', gap:14, alignItems:'flex-start', marginBottom:14 }}>
                <Avatar name={u.name} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:2 }}>{u.name}</div>
                  <div style={{ fontSize:12, color:C.text3, marginBottom:4 }}>{u.email}</div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <span style={{ fontSize:11, padding:'2px 8px', borderRadius:99, background:C.accentLight, color:C.accent, fontWeight:600 }}>
                      {u.role || u.role_name || 'No Role'}
                    </span>
                    <span style={{ fontSize:11, padding:'2px 8px', borderRadius:99, fontWeight:600,
                      background:u.status==='active'?C.accentLight:C.redBg,
                      color:u.status==='active'?C.accent:C.red }}>
                      {u.status}
                    </span>
                  </div>
                </div>
              </div>

              {u.phone && <div style={{ fontSize:12, color:C.text3, marginBottom:12 }}>Phone: {u.phone}</div>}

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <Btn variant="light" onClick={()=>openEdit(u)} style={{ justifyContent:'center', fontSize:12 }}>Edit</Btn>
                <Btn variant="ghost" onClick={()=>{ setPwdUser(u); setNewPwd(''); }} style={{ justifyContent:'center', fontSize:12 }}>Password</Btn>
                <Btn variant={u.status==='active'?'ghost':'light'} onClick={()=>toggleStatus(u)} style={{ justifyContent:'center', fontSize:12 }}>
                  {u.status==='active'?'Deactivate':'Activate'}
                </Btn>
                <Btn variant="danger" onClick={()=>setConfirmDel(u)} style={{ justifyContent:'center', fontSize:12 }}>Delete</Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add User Modal */}
      {showAdd && (
        <Modal title="Add New User" onClose={()=>setShowAdd(false)}>
          <Inp label="Full Name" value={form.name} onChange={v=>set('name',v)} placeholder="Employee full name" />
          <Inp label="Email" value={form.email} onChange={v=>set('email',v)} type="email" placeholder="email@botanix.in" />
          <Inp label="Phone" value={form.phone} onChange={v=>set('phone',v)} placeholder="Mobile number" />
          <Inp label="Role" value={form.role_id} onChange={v=>set('role_id',v)} opts={roleOpts} />
          <Inp label="Status" value={form.status} onChange={v=>set('status',v)} opts={['active','inactive']} />
          <Inp label="Password (default: Botanix@123)" value={form.password} onChange={v=>set('password',v)} type="password" placeholder="Leave blank for default" />
          <div style={{ padding:'10px 14px', background:C.amberBg, borderRadius:8, marginBottom:14, fontSize:12, color:C.amber }}>
            Default password is Botanix@123 if left blank. Ask user to change after first login.
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={addUser} disabled={saving} style={{ flex:1, justifyContent:'center' }}>{saving?'Saving...':'Add User'}</Btn>
            <Btn variant="ghost" onClick={()=>setShowAdd(false)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <Modal title={`Edit - ${editUser.name}`} onClose={()=>setEditUser(null)}>
          <Inp label="Full Name" value={form.name} onChange={v=>set('name',v)} placeholder="Full name" />
          <Inp label="Email" value={form.email} onChange={v=>set('email',v)} type="email" placeholder="Email" />
          <Inp label="Phone" value={form.phone} onChange={v=>set('phone',v)} placeholder="Phone" />
          <Inp label="Role" value={String(form.role_id)} onChange={v=>set('role_id',v)} opts={roleOpts} />
          <Inp label="Status" value={form.status} onChange={v=>set('status',v)} opts={['active','inactive']} />
          <div style={{ padding:'10px 14px', background:C.blueBg, borderRadius:8, marginBottom:14, fontSize:12, color:C.blue }}>
            Changing role will take effect on next login.
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={updateUser} disabled={saving} style={{ flex:1, justifyContent:'center' }}>{saving?'Saving...':'Save Changes'}</Btn>
            <Btn variant="ghost" onClick={()=>setEditUser(null)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Change Password Modal */}
      {pwdUser && (
        <Modal title={`Change Password - ${pwdUser.name}`} onClose={()=>setPwdUser(null)} width="380px">
          <Inp label="New Password" value={newPwd} onChange={setNewPwd} type="password" placeholder="Minimum 6 characters" />
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={changePassword} disabled={saving} style={{ flex:1, justifyContent:'center' }}>{saving?'Saving...':'Change Password'}</Btn>
            <Btn variant="ghost" onClick={()=>setPwdUser(null)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Delete Confirm Modal */}
      {confirmDel && (
        <Modal title="Confirm Delete" onClose={()=>setConfirmDel(null)} width="380px">
          <div style={{ fontSize:14, color:C.text, marginBottom:20 }}>
            Are you sure you want to delete <strong>{confirmDel.name}</strong>? This cannot be undone.
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn variant="danger" onClick={deleteUser} style={{ flex:1, justifyContent:'center' }}>Yes, Delete</Btn>
            <Btn variant="ghost" onClick={()=>setConfirmDel(null)} style={{ flex:1, justifyContent:'center' }}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
